/**
 * Ecosystem Constants — single source of truth
 * ---------------------------------------------------------------------------
 * Any number quoted in marketing UI (tax routing splits, yield scenarios,
 * canonical Pando-Trio caption) must be sourced from here so audit
 * documentation and on-page disclosures never drift.
 */

/* ────────────────────────────────────────────────────────────────── */
/* SolarPando 5% transaction tax routing (public disclosure)          */
/* ────────────────────────────────────────────────────────────────── */

export const SOLAR_TAX_TOTAL_PCT = 5;

export type TaxAllocation = {
  key: "hardware" | "reforestation" | "ops";
  pct: number;
  label: string;
  detail: string;
  accent: string; // OKLCH
};

/**
 * Where the SolarPando 5% transaction tax goes.
 * MUST always sum to SOLAR_TAX_TOTAL_PCT (5). A runtime assertion below
 * catches drift at module load time.
 */
export const SOLAR_TAX_ROUTING: readonly TaxAllocation[] = [
  {
    key: "hardware",
    pct: 3.0, // 60% of the 5% tax
    label: "Hardware capex",
    detail: "Solar panels, batteries, inverters, EV chargers — RWA accumulation.",
    accent: "oklch(0.86 0.16 90)",
  },
  {
    key: "reforestation",
    pct: 1.5, // 30% of the 5% tax
    label: "Reforestation",
    detail: "Native trees geo-tagged and permanently attached to PLANTY collateral.",
    accent: "oklch(0.84 0.16 160)",
  },
  {
    key: "ops",
    pct: 0.5, // 10% of the 5% tax
    label: "Operations",
    detail: "Grid maintenance, monitoring, audit reporting, on-site staff.",
    accent: "oklch(0.92 0.05 90)",
  },
] as const;

/** Share of the total tax that each allocation represents (0–1). */
export function taxShare(a: TaxAllocation): number {
  return a.pct / SOLAR_TAX_TOTAL_PCT;
}

// Module-load sanity check: allocations must sum to the declared total.
{
  const sum = SOLAR_TAX_ROUTING.reduce((s, a) => s + a.pct, 0);
  if (Math.abs(sum - SOLAR_TAX_TOTAL_PCT) > 1e-6) {
    throw new Error(
      `SOLAR_TAX_ROUTING drift: allocations sum to ${sum}%, expected ${SOLAR_TAX_TOTAL_PCT}%`,
    );
  }
}

/* ────────────────────────────────────────────────────────────────── */
/* Yield scenarios — conservative / base / optimal                    */
/* ────────────────────────────────────────────────────────────────── */

export type YieldScenario = "conservative" | "base" | "optimal";

export const YIELD_SCENARIOS: Record<YieldScenario, { pct: number; label: string }> = {
  conservative: { pct: 4, label: "Conservative" },
  base:         { pct: 7, label: "Base" },
  optimal:      { pct: 11, label: "Optimal" },
};

/** Ordered list for UI toggles. */
export const YIELD_SCENARIO_ORDER: readonly YieldScenario[] = [
  "conservative",
  "base",
  "optimal",
] as const;

/**
 * Per-asset multiplier around the global scenario band. Lets water/land
 * skew slightly higher/lower than solar or governance without diverging
 * from the network-wide 4/7/11 story.
 */
export const ASSET_YIELD_MULTIPLIER: Record<string, number> = {
  solar: 1.0,
  aqua: 0.9,
  planty: 1.1,
  village: 0.85,
  meal24: 1.15,
};

export function scenarioApyFor(assetKey: string, scenario: YieldScenario): number {
  const mult = ASSET_YIELD_MULTIPLIER[assetKey] ?? 1;
  return YIELD_SCENARIOS[scenario].pct * mult;
}

/* ────────────────────────────────────────────────────────────────── */
/* Pando-Trio directional caption (canonical wording — do not fork)   */
/* ────────────────────────────────────────────────────────────────── */

export const PANDO_TRIO_CAPTION =
  "Land feeds Water. Water feeds Power. Power buys back the Meal. Meals fund the Village.";

/** Explicit ordered flow used by the Nexus cockpit's directional arrows. */
export const PANDO_TRIO_FLOW: readonly {
  from: "planty" | "aqua" | "solar" | "sfn";
  to: "aqua" | "solar" | "sfn" | "village";
  legend: string;
}[] = [
  { from: "planty", to: "aqua",    legend: "Land → Water" },
  { from: "aqua",   to: "solar",   legend: "Water → Power" },
  { from: "solar",  to: "sfn",     legend: "Power → Meal buyback" },
  { from: "sfn",    to: "village", legend: "Meals → Village" },
] as const;

/* ────────────────────────────────────────────────────────────────── */
/* Official SFN Project Treasury (MEAL2 + MEAL4 purchase destination) */
/* ────────────────────────────────────────────────────────────────── */

/**
 * Canonical Solana destination address for ALL MEAL2 and MEAL4 token
 * purchase transactions. Never fork this value per component.
 */
export const SFN_TREASURY_ADDRESS =
  "cXowtRhtzZcHe87g3MSyBGa7KvT9N94JZLCDm8eASMp";

/** Display helper: 4…4 truncation for tight UI rows. */
export function shortTreasury(addr: string = SFN_TREASURY_ADDRESS) {
  return addr.length > 12 ? `${addr.slice(0, 4)}…${addr.slice(-4)}` : addr;
}

/**
 * Locked issuance prices (USD per token). MEAL2 is the $1 daily-utility unit;
 * MEAL4 is issued at $0.10. Used to re-derive the token side of a settlement
 * during on-chain receipt verification.
 */
export const MEAL2_USD_PRICE = 1;
export const MEAL4_USD_PRICE = 0.1;
